import { useEffect } from 'react';
import { useBlocker, useNavigate } from 'react-router-dom';
import { auth } from '@/config/firebase';
import { analyticsService } from '@/services/analytics/analyticsService';
import { useLocalization } from '@/hooks/useLocalization';
import { ROUTES } from '@/app/router/routes';
import { CheckCircleOutlineIcon } from './components/AuthIcons';
import styles from './EmailVerifiedPage.module.css';

/**
 * EmailVerifiedPage — success confirmation after email verification.
 *
 * Mirrors Flutter email_verified_page.dart.
 *
 * Shows a success message with a "Click here to continue" link, centered on a white page with
 * no header or footer, as in Flutter.
 * If the user is logged in, reloads to home.
 * If the user is not logged in, navigates to login.
 *
 * Flutter listens to the browser's popstate here and goes back again, so Back can't return
 * to the verification link. React blocks the Back / Forward navigation instead, keeping the
 * user on this page (only while it is shown; Flutter never removes its listener).
 *
 * Flutter source files:
 *   - presentation/web/auth/verify_email_signup/email_verified_page.dart
 */

export function EmailVerifiedPage() {
  const navigate = useNavigate();
  const { t } = useLocalization();

  // Back-button trap — only history (POP) navigations are blocked
  const blocker = useBlocker(({ historyAction }) => historyAction === 'POP');
  useEffect(() => {
    if (blocker.state === 'blocked') blocker.reset();
  }, [blocker]);

  function handleContinue() {
    if (auth.currentUser) {
      // Reload the page to home route (mirrors Flutter: window.location.href = '/')
      window.location.href = '/';
    } else {
      void analyticsService.logScreenView({ screenName: ROUTES.LOGIN });
      navigate(ROUTES.LOGIN, { replace: true });
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <CheckCircleOutlineIcon className={styles.successIcon} />

        <h1 className={styles.title}>{t('Email Verified!')}</h1>
        <p className={styles.subtitle}>{t('Your email address was successfully verified.')}</p>

        <button type="button" onClick={handleContinue} className={styles.link}>
          {t('Click here to continue')}
        </button>
      </div>
    </div>
  );
}
